import React from 'react';
import { Box, CheckboxV2, Flex, IconV2, TextV2 } from '@withjoy/joykit';
import { Lock } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import type { PayoutDetailsConfirmationProps } from './PayoutDetailsConfirmation.types';

const LOCKED_SLOT = '__LOCKED__';

/** Shown by both payout setup forms (registry and website cash funds) so hosts confirm the details Stripe locks on save. */
export const PayoutDetailsConfirmation: React.FC<PayoutDetailsConfirmationProps> = ({ countryLabel, currencyCode, isConfirmed, onConfirmedChange }) => {
  const { tq } = useTranslation('cashFundsShared');
  const [acknowledgementBefore, acknowledgementAfter = ''] =
    tq`I confirm my legal name is correct and my bank account is in ${countryLabel}. I understand this ${LOCKED_SLOT}.`.split(LOCKED_SLOT);

  return (
    <Flex
      flexDirection="column"
      gap={6}
    >
      <Flex
        as="output"
        alignItems="flex-start"
        gap={4}
      >
        <Box
          position="relative"
          flex="0 0 auto"
        >
          <Flex
            alignItems="center"
            justifyContent="center"
            width="40px"
            height="40px"
            borderRadius="full"
            backgroundColor="mono3"
          >
            <TextV2
              typographyVariant="hed1"
              color="mono14"
            >
              {getCurrencySymbolFromCurrencyCode(currencyCode)}
            </TextV2>
          </Flex>
          <Flex
            position="absolute"
            right="-4px"
            bottom="-4px"
            alignItems="center"
            justifyContent="center"
            width="20px"
            height="20px"
            borderRadius="full"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="mono4"
            backgroundColor="mono1"
          >
            <IconV2
              size={12}
              color="mono12"
            >
              <Lock />
            </IconV2>
          </Flex>
        </Box>
        <Flex
          flexDirection="column"
          gap={1}
          minWidth={0}
        >
          <TextV2
            typographyVariant="hed1"
            color="mono14"
          >
            {tq`Payouts settle in ${currencyCode}`}
          </TextV2>
          <TextV2
            typographyVariant="label2"
            color="mono10"
          >
            {tq`Your bank account must be in ${countryLabel}. If it doesn't accept ${currencyCode}, Stripe will convert your payouts and extra fees may apply.`}
          </TextV2>
        </Flex>
      </Flex>
      <Box
        padding={5}
        borderWidth="1px"
        borderStyle="solid"
        borderColor="mono3"
        borderRadius={3}
      >
        <CheckboxV2
          isChecked={isConfirmed}
          onChange={event => onConfirmedChange(event.target.checked)}
        >
          <TextV2
            as="span"
            typographyVariant="label2"
            color="mono14"
          >
            {acknowledgementBefore}
            <strong>{tq`can't be changed after saving`}</strong>
            {acknowledgementAfter}
          </TextV2>
        </CheckboxV2>
      </Box>
    </Flex>
  );
};
